import {Customer} from '@hconnect/apiclient'
import {UseQueryResult} from '@tanstack/react-query'
import {useCallback} from 'react'
import {UseFormReturn} from 'react-hook-form'

import {useTranslation} from '../../../hooks/useTranslation'
import {RoleOptions} from '../../../layouts/Roles/RoleEditLayout'
import {payerActivatedRoles} from '../../../layouts/Roles/utils'
import {GroupedRoleAssignment} from '../../../modules/ManageUsers.selectors'
import {Permission} from "../../../modules/LoggedInUser";
import {useCustomerAdminOptions} from "../../../layouts/Roles/hooks/useCustomerAdminOptions";

type UseRoleFormSubmissionProps = {
  customerAdminQueryInfo: UseQueryResult<Customer[]>
  selectedOptions: RoleOptions
  setSelectedOptions: (options: RoleOptions) => void
  handleRoleAdded: (values: GroupedRoleAssignment) => Promise<void>
  handleRoleUpdated: (values: GroupedRoleAssignment) => void
  loggedInUserPermissions?: Permission[] | null
  refetchRoles?: () => Promise<any>
  setIsEditing: (editing: boolean) => void
  form: UseFormReturn<GroupedRoleAssignment>
  isUserCreation?: boolean
  dataScopeAccessError?: string | null
}

export const useRoleFormSubmission = ({
  customerAdminQueryInfo,
  selectedOptions,
  setSelectedOptions,
  handleRoleAdded,
  refetchRoles,
  setIsEditing,
  form,
  isUserCreation = false,
  loggedInUserPermissions,
  dataScopeAccessError = null
}: UseRoleFormSubmissionProps) => {
  const {t} = useTranslation()

    const {
        businessLineOptions,
        countryIdOptions,
        orgUnitIdOptions
    } = useCustomerAdminOptions(selectedOptions, setSelectedOptions, loggedInUserPermissions)

  const handleSubmit =
    // eslint-disable-next-line complexity
    async (formData: GroupedRoleAssignment) => {
      try {
        form.clearErrors()

        const {
          roleType,
          variantDataScopes: {customersScopes, payerIds, haulierIds}
        } = formData

        if (dataScopeAccessError) {
          form.setError('root', {
            type: 'validation',
            message: t(dataScopeAccessError)
          })
          return
        }

        if (customerAdminQueryInfo.data && customerAdminQueryInfo.data?.length > 0) {
            const countryIdValue = !selectedOptions.countryId.isSingle
                ? selectedOptions.countryId?.value || ''
                : countryIdOptions?.[0]?.key || ''
            const businessLineValue = !selectedOptions.businessLine.isSingle
                ? selectedOptions.businessLine?.value || ''
                : businessLineOptions?.[0]?.key || ''
            const orgUnitIdValue = !selectedOptions.orgUnitId.isSingle
                ? selectedOptions.orgUnitId?.value || ''
                : orgUnitIdOptions?.[0]?.key || ''

          formData.commonDataScopes['countryId'] = countryIdValue
          formData.commonDataScopes['businessLine'] = businessLineValue
          formData.commonDataScopes['orgUnitId'] = orgUnitIdValue
        }

        const neitherCustomerNorPayerPresent =
          customersScopes.length === 0 && (!payerIds || payerIds?.length === 0)

        const neitherCustomerNorHaulierPresent =
          customersScopes.length === 0 && (!haulierIds || haulierIds?.length === 0)

        if (
          payerActivatedRoles.includes(roleType) &&
          roleType !== 'SALES_AGENT' &&
          neitherCustomerNorPayerPresent
        ) {
          form.setError('root', {
            type: 'validation',
            message: t('roleAssignment.customerOrPayerRequired')
          })
          return
        }

        if (roleType === 'DATALINK_DEVELOPER' && neitherCustomerNorHaulierPresent) {
          form.setError('root', {
            type: 'validation',
            message: t('roleAssignment.customerOrHaulierRequired')
          })
          return
        }

        if (isUserCreation) {
          await handleRoleAdded(formData)
          setIsEditing(false)
          form.reset()
        } else {
          await handleRoleAdded(formData)
          setIsEditing(false)

          if (refetchRoles) {
            await refetchRoles()
          }
        }
      } catch (error) {
        form.setError('root', {
          type: 'server',
          message: error instanceof Error ? error.message : 'An error occurred'
        })
      }
    }

  return {
    handleSubmit
  }
}
